import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { useApi } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import { useMutate } from "@/shared/hooks";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { GetTableUsers, TableApiCallParams } from "@/shared/types";
import type {
  ChangePermissionsRequest,
  ChangeUserPasswordRequest,
  CreateInviteCodeRequest,
  ManagersResponse,
  UserIdRequest,
  UsersResponse,
} from "./api.dto";
import type { PaginatedResponse } from "@/shared/types";
import type { User } from "@/entities/user";

export const useManagers = (props: TableApiCallParams) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<ManagersResponse>>({
    queryKey: ["managers", JSON.stringify(filterObjectKeys(params))],
    queryFn: async () => {
      const { data } = await api.get("/access/managers", {
        params,
      });
      return data;
    },
  });
};

export const useCreateInviteCode = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/access/create",
    toast: {
      success: {
        description: "Код создан.",
      },
    },
  });

  return useMutation<AxiosResponse, AxiosError, CreateInviteCodeRequest>({
    mutationKey: ["createInviteCode"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useChangeUserPassword = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "put",
    url: "/access/change-password",
    toast: {
      success: {
        description: "Пароль изменён.",
      },
    },
  });

  return useMutation<AxiosResponse, AxiosError, ChangeUserPasswordRequest>({
    mutationKey: ["userChangePassword"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useChangePermissions = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Доступы обновлены",
      },
    },
  });
  const { api } = useApi();

  return useMutation<AxiosResponse, AxiosError, ChangePermissionsRequest>({
    mutationKey: ["userChangePassword"],
    mutationFn: ({ userId, ...data }) => {
      return api.patch(`/access/${userId}/permissions`, data);
    },
    onSuccess,
    onError,
  });
};

export const useBanUser = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "patch",
    url: "/access/ban",
    toast: {
      success: {
        description: "Пользователь забанен.",
      },
    },
    refetchQueries: [["users"], ["managers"]],
  });

  return useMutation<AxiosResponse, AxiosError, UserIdRequest>({
    mutationKey: ["banUser"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useUnbanUser = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "patch",
    url: "/access/unban",
    toast: {
      success: {
        description: "Пользователь разбанен.",
      },
    },
    refetchQueries: [["users"], ["managers"]],
  });

  return useMutation<AxiosResponse, AxiosError, UserIdRequest>({
    mutationKey: ["unbanUser"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useUsersInfiniteScroll = (
  params: Omit<GetTableUsers, "currentPage">,
) => {
  const { api } = useApi();

  return useInfiniteQuery<PaginatedResponse<User>>({
    queryKey: ["usersInfinite", params],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await api.get("/access/users", {
        params: { ...filterObjectKeys(params), currentPage: pageParam },
      });

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = params.pageSize;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });
};

export const useUsers = (props: GetTableUsers) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<UsersResponse>>({
    queryKey: ["users", params],
    queryFn: async () => {
      const response = await api.get("/access/users", {
        params,
      });

      return response.data;
    },
  });
};
